import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { AuthService } from '@cardapio-online/auth';
import { NotificationDispatcherService } from '@cardapio-online/notifications';
import { AdminNavItem } from '../../../models/admin-nav-item.model';
import { SystemConfigService } from '../../../system/system-config.service';
import { ThemeApplierService } from '../../../theme/theme-applier.service';
import { AdminMenuEditService } from '../service/admin-menu-edit.service';
import { AdminNavigationService } from '../service/admin-navigation.service';

@Component({
  selector: 'lib-shell-layout-admin',
  templateUrl: './shell-layout-admin.component.html',
  styleUrl: './shell-layout-admin.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellLayoutAdminComponent implements OnInit {
  private readonly themeApplierService = inject(ThemeApplierService);
  private readonly authService = inject(AuthService);
  private readonly adminNavigationService = inject(AdminNavigationService);
  private readonly adminMenuEditService = inject(AdminMenuEditService);
  private readonly systemConfigService = inject(SystemConfigService);
  private readonly notificationDispatcherService = inject(
    NotificationDispatcherService
  );

  protected readonly isProfilePanelOpen = signal(false);
  protected readonly isSidebarOpen = signal(false);
  protected readonly isMenuEditing = this.adminMenuEditService.isEditing;
  protected readonly systemConfig = this.systemConfigService.config;
  protected readonly isDarkTheme = computed(
    () => this.systemConfig().themeMode === 'dark'
  );
  protected readonly menuEditingEnabled = computed(
    () => this.systemConfig().menuEditingEnabled
  );
  protected readonly resolvedNavItems = computed(() =>
    this.adminMenuEditService.items()
  );

  protected readonly currentUser = {
    name: 'David Stewart',
    role: 'Administrador',
    photoUrl: 'assets/brand/empty.jpg',
  };

  @Input() navItems: AdminNavItem[] | null = null;
  @Input() brandTitle = 'Ju Marmitaria';
  @Input() brandSubtitle = 'Painel administrativo';

  protected readonly toolbarChips = [
    { label: 'Ambiente', value: 'Produção' },
    { label: 'Tenant', value: 'Demo Company' },
    { label: 'Plano', value: 'Enterprise' },
  ];

  private readonly menuEditingSync = effect(() => {
    if (!this.menuEditingEnabled()) {
      this.adminMenuEditService.cancelEditing();
    }
  });

  ngOnInit(): void {
    this.themeApplierService.apply();
    this.notificationDispatcherService.start();

    const baseNavItems =
      this.navItems?.length
        ? this.navItems
        : this.adminNavigationService.getAdminNavItems();

    this.adminMenuEditService.initialize(baseNavItems);
  }

  protected toggleSidebar(): void {
    this.isSidebarOpen.update((value) => !value);
  }

  protected closeSidebar(): void {
    this.isSidebarOpen.set(false);
  }

  protected toggleProfilePanel(): void {
    this.isProfilePanelOpen.update((value) => !value);
  }

  protected closeProfilePanel(): void {
    this.isProfilePanelOpen.set(false);
  }

  protected startMenuEditing(): void {
    if (!this.menuEditingEnabled()) {
      return;
    }

    this.adminMenuEditService.startEditing();
  }

  protected cancelMenuEditing(): void {
    this.adminMenuEditService.cancelEditing();
  }

  protected saveMenuEditing(): void {
    this.adminMenuEditService.saveEditing();
  }

  protected onMenuDraftReordered(items: AdminNavItem[]): void {
    this.adminMenuEditService.updateDraft(items);
  }

  protected goToProfile(): void {
    this.closeProfilePanel();
    window.location.href = '/admin/profile';
  }

  protected toggleTheme(): void {
    this.themeApplierService.toggle();
  }

  protected logout(): void {
    this.authService.logout();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (window.innerWidth > 1100 && this.isSidebarOpen()) {
      this.closeSidebar();
    }
  }
}
